import { useState, useEffect, useCallback } from 'react';
import {
  Store,
  Layout,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Settings,
  Image,
  FolderOpen,
  Check,
  ShoppingBag,
  Eye,
  Save,
  Palette,
  Globe,
  PlusCircle,
  FileText,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

// Types matching database schema and storefront layout
interface Block {
  id?: string;
  type: string;
  sortOrder: number;
  content: any;
}

interface Section {
  id?: string;
  type: string;
  sortOrder: number;
  settings: any;
  blocks: Block[];
}

interface Page {
  id: string;
  title: string;
  slug: string;
  isPublished: boolean;
  isSystem: boolean;
  seoTitle?: string;
  seoDescription?: string;
  sections?: Section[];
}

interface Product {
  id?: string;
  title: string;
  slug: string;
  description: string;
  price: number;
  stock: number;
  images: string[];
  isActive: boolean;
}

interface Organization {
  id: number;
  name: string;
  slug: string;
  brand_color: string;
  whatsapp_number?: string;
  custom_domain?: string;
}

export default function StoreBuilder() {
  const { user } = useAuth();
  
  // App UI Tab States
  const [activeTab, setActiveTab] = useState<'canvas' | 'products' | 'branding' | 'settings'>('canvas');
  
  // Data States
  const [org, setOrg] = useState<Organization | null>(null);
  const [pages, setPages] = useState<Page[]>([]);
  const [selectedPage, setSelectedPage] = useState<Page | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  
  // Loading & Action States
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  
  // Active Selected Element for editing properties
  const [selectedSectionIdx, setSelectedSectionIdx] = useState<number | null>(null);
  const [selectedBlockIdx, setSelectedBlockIdx] = useState<{ secIdx: number; blkIdx: number } | null>(null);

  // New Item Temporary States
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');

  // Product CRUD Form States
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [prodTitle, setProdTitle] = useState('');
  const [prodSlug, setProdSlug] = useState('');
  const [prodPrice, setProdPrice] = useState('0');
  const [prodStock, setProdStock] = useState('100');
  const [prodDesc, setProdDesc] = useState('');
  const [prodImage, setProdImage] = useState('');
  const [prodActive, setProdActive] = useState(true);

  // ---------------------------------------------------------
  // 1. Data Fetchers
  // ---------------------------------------------------------
  
  const loadStoreData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      // Step A: Resolve Merchant Organization via user id mapping
      const ownerId = user.pgUserId || user.id;
      const orgRes = await apiFetch<{ organization: Organization }>(
        `/api/admin/store-builder/organization?ownerId=${ownerId}`
      );
      
      if (!orgRes?.organization) {
        throw new Error('Could not find or auto-seed a storefront organization for your profile.');
      }
      
      const activeOrg = orgRes.organization;
      setOrg(activeOrg);
      
      const tenantId = String(activeOrg.id);
      
      // Step B: Fetch storefront pages
      const pagesRes = await apiFetch<{ pages: Page[] }>(
        `/api/admin/store-builder/pages?tenantId=${tenantId}`,
        { headers: { 'x-tenant-id': tenantId } }
      );
      setPages(pagesRes.pages || []);
      
      // Step C: Fetch storefront products catalog
      const prodRes = await apiFetch<{ products: Product[] }>(
        `/api/admin/store-builder/products?tenantId=${tenantId}`,
        { headers: { 'x-tenant-id': tenantId } }
      );
      setProducts(prodRes.products || []);
      
      // Select the home page by default
      if (pagesRes.pages && pagesRes.pages.length > 0) {
        const homePage = pagesRes.pages.find((p) => p.slug === 'home') || pagesRes.pages[0];
        await loadPageLayout(homePage.id, tenantId);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to initialize builder data.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadStoreData();
  }, [loadStoreData]);

  // Fetch dynamic sections and blocks for the selected page
  const loadPageLayout = async (pageId: string, tenantId?: string) => {
    const activeTenantId = tenantId || (org ? String(org.id) : '');
    if (!activeTenantId) return;
    
    setError(null);
    try {
      const pageRes = await apiFetch<{ page: Page }>(
        `/api/admin/store-builder/pages/${pageId}?tenantId=${activeTenantId}`,
        { headers: { 'x-tenant-id': activeTenantId } }
      );
      setSelectedPage(pageRes.page);
      setSelectedSectionIdx(null);
      setSelectedBlockIdx(null);
    } catch (err: any) {
      setError('Failed to load page layout template.');
    }
  };

  // ---------------------------------------------------------
  // 2. Visual Layout Builder Actions (PUT Page Layout Canvas)
  // ---------------------------------------------------------

  const saveStorefront = async () => {
    if (!org || !selectedPage) return;
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const tenantId = String(org.id);
      
      // A. Save the current layout sections/blocks canvas to the pages API
      await apiFetch(`/api/admin/store-builder/pages/${selectedPage.id}?tenantId=${tenantId}`, {
        method: 'PUT',
        headers: { 'x-tenant-id': tenantId },
        body: JSON.stringify({
          title: selectedPage.title,
          slug: selectedPage.slug,
          isPublished: selectedPage.isPublished,
          seoTitle: selectedPage.seoTitle,
          seoDescription: selectedPage.seoDescription,
          sections: selectedPage.sections || [],
        }),
      });

      // B. Save organization metadata details (brand settings, domain, whatsapp)
      await apiFetch(`/api/admin/store-builder/organization?tenantId=${tenantId}`, {
        method: 'PUT',
        headers: { 'x-tenant-id': tenantId },
        body: JSON.stringify(org),
      });

      setSuccessMsg('Storefront successfully updated and live!');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  // Sections Sorting / Mutations
  const addSection = (type: 'HERO' | 'FEATURED_PRODUCTS' | 'RICHTEXT' | 'BANNER') => {
    if (!selectedPage) return;
    
    const newSection: Section = {
      type,
      sortOrder: (selectedPage.sections?.length || 0),
      settings: 
        type === 'HERO' ? { alignment: 'center', overlayOpacity: 0.6 } :
        type === 'FEATURED_PRODUCTS' ? { title: 'Featured items', subtitle: 'Handpicked favorites' } :
        type === 'RICHTEXT' ? { paddingY: 'py-16', background: 'bg-white' } :
        { title: 'Promo banner', subtitle: 'Details here' },
      blocks: [],
    };

    const updatedSections = [...(selectedPage.sections || []), newSection];
    setSelectedPage({ ...selectedPage, sections: updatedSections });
    setSelectedSectionIdx(updatedSections.length - 1);
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    if (!selectedPage || !selectedPage.sections) return;
    const list = [...selectedPage.sections];
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIdx < 0 || targetIdx >= list.length) return;
    
    const temp = list[index];
    list[index] = list[targetIdx];
    list[targetIdx] = temp;
    
    // Reset sortOrder indices
    const updated = list.map((sec, i) => ({ ...sec, sortOrder: i }));
    setSelectedPage({ ...selectedPage, sections: updated });
    setSelectedSectionIdx(targetIdx);
  };

  const removeSection = (index: number) => {
    if (!selectedPage || !selectedPage.sections) return;
    const list = selectedPage.sections.filter((_, i) => i !== index);
    const updated = list.map((sec, i) => ({ ...sec, sortOrder: i }));
    setSelectedPage({ ...selectedPage, sections: updated });
    setSelectedSectionIdx(null);
    setSelectedBlockIdx(null);
  };

  // Blocks Inside Sections Handling
  const addBlock = (secIdx: number, type: string) => {
    if (!selectedPage || !selectedPage.sections) return;
    
    const sec = selectedPage.sections[secIdx];
    const newBlock: Block = {
      type,
      sortOrder: sec.blocks.length,
      content: 
        type === 'HEADING' ? { text: 'New Heading Line' } :
        type === 'BODY' ? { text: 'Description text content details.' } :
        type === 'BUTTON' ? { text: 'Click Here', url: '/catalog' } :
        type === 'HERO_SLIDE' ? { headline: 'Dynamic Title', tagline: 'New Offer', ctaText: 'Shop Now', ctaUrl: '/catalog' } :
        type === 'PRODUCT_CARD' ? { title: 'Product Item', price: 99.00, category: 'Uncategorized', imageUrl: '' } :
        { title: 'Promo title', subtitle: 'Subtitle details' },
    };

    const updatedSecs = selectedPage.sections.map((s, idx) => {
      if (idx !== secIdx) return s;
      return { ...s, blocks: [...s.blocks, newBlock] };
    });

    setSelectedPage({ ...selectedPage, sections: updatedSecs });
    setSelectedBlockIdx({ secIdx, blkIdx: updatedSecs[secIdx].blocks.length - 1 });
  };

  const removeBlock = (secIdx: number, blkIdx: number) => {
    if (!selectedPage || !selectedPage.sections) return;
    
    const updatedSecs = selectedPage.sections.map((s, idx) => {
      if (idx !== secIdx) return s;
      const filteredBlocks = s.blocks.filter((_, bIdx) => bIdx !== blkIdx);
      return {
        ...s,
        blocks: filteredBlocks.map((b, i) => ({ ...b, sortOrder: i })),
      };
    });

    setSelectedPage({ ...selectedPage, sections: updatedSecs });
    setSelectedBlockIdx(null);
  };

  // ---------------------------------------------------------
  // 3. Page CRUD (POST New Page)
  // ---------------------------------------------------------

  const createPage = async () => {
    if (!org || !newPageTitle || !newPageSlug) return;
    setSaving(true);
    setError(null);
    try {
      const tenantId = String(org.id);
      const res = await apiFetch<{ page: Page }>(
        `/api/admin/store-builder/pages?tenantId=${tenantId}`,
        {
          method: 'POST',
          headers: { 'x-tenant-id': tenantId },
          body: JSON.stringify({
            title: newPageTitle,
            slug: newPageSlug,
            seoTitle: `${newPageTitle} | ${org.name}`,
            seoDescription: `Welcome to ${newPageTitle} storefront page.`,
          }),
        }
      );
      
      if (res?.page) {
        setPages([...pages, res.page]);
        setSelectedPage(res.page);
        setShowNewPageModal(false);
        setNewPageTitle('');
        setNewPageSlug('');
        setSuccessMsg('New storefront page registered!');
        setTimeout(() => setSuccessMsg(null), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create new page.');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------
  // 4. Products CRUD Operations
  // ---------------------------------------------------------

  const openEditProduct = (prod: Product | null) => {
    if (prod) {
      setEditingProduct(prod);
      setProdTitle(prod.title);
      setProdSlug(prod.slug);
      setProdPrice(String(prod.price));
      setProdStock(String(prod.stock));
      setProdDesc(prod.description);
      setProdImage(prod.images[0] || '');
      setProdActive(prod.isActive);
    } else {
      setEditingProduct(null);
      setProdTitle('');
      setProdSlug('');
      setProdPrice('49.99');
      setProdStock('100');
      setProdDesc('');
      setProdImage('');
      setProdActive(true);
    }
  };

  const saveProduct = async () => {
    if (!org) return;
    if (!prodTitle || !prodSlug) {
      setError('Product title and slug are required fields.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const tenantId = String(org.id);
      const payload = {
        id: editingProduct?.id,
        title: prodTitle,
        slug: prodSlug,
        description: prodDesc,
        price: parseFloat(prodPrice) || 0,
        stock: parseInt(prodStock, 10) || 0,
        images: prodImage ? [prodImage] : [],
        isActive: prodActive,
      };

      const res = await apiFetch<{ product: Product }>(
        `/api/admin/store-builder/products?tenantId=${tenantId}`,
        {
          method: 'POST',
          headers: { 'x-tenant-id': tenantId },
          body: JSON.stringify(payload),
        }
      );

      if (res?.product) {
        if (editingProduct) {
          setProducts(products.map((p) => (p.id === editingProduct.id ? res.product : p)));
        } else {
          setProducts([res.product, ...products]);
        }
        openEditProduct(null); // Clear form
        setSuccessMsg('Product catalog successfully saved!');
        setTimeout(() => setSuccessMsg(null), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save product catalogue item.');
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id: string) => {
    if (!org) return;
    if (!confirm('Are you sure you want to delete this product? This will permanently remove it from the catalog.')) return;
    
    setSaving(true);
    setError(null);
    try {
      const tenantId = String(org.id);
      await apiFetch(`/api/admin/store-builder/products?tenantId=${tenantId}&id=${id}`, {
        method: 'DELETE',
        headers: { 'x-tenant-id': tenantId },
      });
      setProducts(products.filter((p) => p.id !== id));
      setSuccessMsg('Product permanently deleted.');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to remove item.');
    } finally {
      setSaving(false);
    }
  };

  // Helper Auto-slug generator
  const handleTitleChange = (val: string, type: 'page' | 'prod') => {
    if (type === 'page') {
      setNewPageTitle(val);
      setNewPageSlug(val.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-'));
    } else {
      setProdTitle(val);
      setProdSlug(val.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-'));
    }
  };

  // ---------------------------------------------------------
  // 5. Layout Rendering Simulator Helpers
  // ---------------------------------------------------------

  if (loading) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-3">
        <Loader2 className="h-10 w-10 animate-spin text-brand" />
        <p className="text-sm text-zinc-400">Loading dynamic storefront customizer...</p>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col gap-6 text-zinc-200">
      {/* Visual Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/[0.08] pb-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
            <Store className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">Storefront Visual Customizer</h1>
            <p className="text-xs text-zinc-400">
              Headless JSON dynamic design engine — running store: <strong className="text-zinc-200">{org?.name}</strong>
            </p>
          </div>
        </div>

        {/* Global Action Tools */}
        <div className="flex items-center gap-3">
          {org?.custom_domain && (
            <a
              href={`https://${org.custom_domain}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-xs font-semibold hover:bg-white/10"
            >
              <Eye className="h-3.5 w-3.5" />
              Preview Live
            </a>
          )}
          <button
            onClick={saveStorefront}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-xs font-bold text-white hover:bg-brand-hover disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Message Notifications */}
      {error && (
        <div className="flex items-center gap-3 rounded-lg border border-red-500/20 bg-red-500/5 p-4 text-xs text-red-400">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4 text-xs text-emerald-400">
          <Check className="h-4 w-4 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex border-b border-white/[0.08]">
        <button
          onClick={() => setActiveTab('canvas')}
          className={`flex items-center gap-2 border-b-2 px-5 py-3 text-xs font-semibold transition-all ${
            activeTab === 'canvas' ? 'border-brand text-brand' : 'border-transparent text-zinc-400 hover:text-zinc-200'
          }`}
        >
          <Layout className="h-4 w-4" />
          Layout Canvas
        </button>
        <button
          onClick={() => setActiveTab('products')}
          className={`flex items-center gap-2 border-b-2 px-5 py-3 text-xs font-semibold transition-all ${
            activeTab === 'products' ? 'border-brand text-brand' : 'border-transparent text-zinc-400 hover:text-zinc-200'
          }`}
        >
          <ShoppingBag className="h-4 w-4" />
          Products Catalog
        </button>
        <button
          onClick={() => setActiveTab('branding')}
          className={`flex items-center gap-2 border-b-2 px-5 py-3 text-xs font-semibold transition-all ${
            activeTab === 'branding' ? 'border-brand text-brand' : 'border-transparent text-zinc-400 hover:text-zinc-200'
          }`}
        >
          <Palette className="h-4 w-4" />
          Theme & Branding
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex items-center gap-2 border-b-2 px-5 py-3 text-xs font-semibold transition-all ${
            activeTab === 'settings' ? 'border-brand text-brand' : 'border-transparent text-zinc-400 hover:text-zinc-200'
          }`}
        >
          <Globe className="h-4 w-4" />
          Domain & Settings
        </button>
      </div>

      {/* ==============================================================================
          TAB 1: LAYOUT CANVAS & VISUAL PREVIEW SIMULATOR
          ============================================================================== */}
      {activeTab === 'canvas' && selectedPage && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          
          {/* Left panel: Route pages & Sections order list */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            
            {/* Page Selector widget */}
            <div className="rounded-2xl border border-white/[0.08] bg-dark-panel p-5">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Store Pages
                </span>
                <button
                  onClick={() => setShowNewPageModal(true)}
                  className="text-brand hover:underline text-xs flex items-center gap-1 font-semibold"
                >
                  <Plus className="h-3 w-3" />
                  New Page
                </button>
              </div>
              
              <div className="flex flex-col gap-2">
                {pages.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => org && loadPageLayout(p.id)}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 text-left text-xs font-semibold transition-all ${
                      selectedPage.id === p.id
                        ? 'bg-brand/10 border border-brand/20 text-brand'
                        : 'border border-transparent hover:bg-white/5 text-zinc-400 hover:text-zinc-200'
                    }`}
                  >
                    <span>{p.title}</span>
                    <span className="font-mono text-[10px] text-zinc-500">/{p.slug === 'home' ? '' : p.slug}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Sections Canvas Outline */}
            <div className="rounded-2xl border border-white/[0.08] bg-dark-panel p-5 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Layout Sections</span>
                <span className="text-[10px] bg-white/5 px-2 py-0.5 rounded font-bold text-zinc-500">
                  {selectedPage.sections?.length || 0} Total
                </span>
              </div>

              {/* Sections list */}
              <div className="flex flex-col gap-3">
                {selectedPage.sections?.map((sec, sIdx) => (
                  <div
                    key={sIdx}
                    onClick={() => {
                      setSelectedSectionIdx(sIdx);
                      setSelectedBlockIdx(null);
                    }}
                    className={`group/sec relative flex flex-col rounded-xl border p-4 cursor-pointer transition-all ${
                      selectedSectionIdx === sIdx
                        ? 'bg-zinc-800/80 border-brand/40 shadow-lg'
                        : 'bg-white/5 border-white/[0.08] hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono text-zinc-500">#{sIdx + 1}</span>
                        <span className="text-xs font-bold text-zinc-300">{sec.type}</span>
                      </div>
                      
                      {/* Sort/Delete controls */}
                      <div className="hidden group-hover/sec:flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); moveSection(sIdx, 'up'); }}
                          disabled={sIdx === 0}
                          className="p-1 hover:bg-white/10 rounded text-zinc-400 hover:text-zinc-200 disabled:opacity-30"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); moveSection(sIdx, 'down'); }}
                          disabled={sIdx === (selectedPage.sections?.length || 0) - 1}
                          className="p-1 hover:bg-white/10 rounded text-zinc-400 hover:text-zinc-200 disabled:opacity-30"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeSection(sIdx); }}
                          className="p-1 hover:bg-red-500/10 rounded text-zinc-400 hover:text-red-400"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>

                    {/* Sub-Blocks mapping inside Section */}
                    {sec.blocks && sec.blocks.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {sec.blocks.map((blk, bIdx) => (
                          <span
                            key={bIdx}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSectionIdx(sIdx);
                              setSelectedBlockIdx({ secIdx: sIdx, blkIdx: bIdx });
                            }}
                            className={`px-2 py-0.5 rounded text-[9px] font-mono border transition-all ${
                              selectedBlockIdx?.secIdx === sIdx && selectedBlockIdx?.blkIdx === bIdx
                                ? 'bg-brand/20 border-brand text-brand font-bold'
                                : 'bg-white/5 border-white/10 text-zinc-500 hover:text-zinc-300'
                            }`}
                          >
                            {blk.type}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Add nested block button */}
                    <div className="mt-3 border-t border-white/[0.05] pt-2 flex justify-between items-center">
                      <span className="text-[9px] text-zinc-500 font-semibold uppercase">Blocks</span>
                      <div className="flex gap-2">
                        {sec.type === 'HERO' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); addBlock(sIdx, 'HERO_SLIDE'); }}
                            className="text-[9px] text-brand hover:underline font-semibold"
                          >
                            + Hero Slide
                          </button>
                        )}
                        {sec.type === 'FEATURED_PRODUCTS' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); addBlock(sIdx, 'PRODUCT_CARD'); }}
                            className="text-[9px] text-brand hover:underline font-semibold"
                          >
                            + Product Card
                          </button>
                        )}
                        {sec.type === 'RICHTEXT' && (
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); addBlock(sIdx, 'HEADING'); }}
                              className="text-[9px] text-brand hover:underline font-semibold"
                            >
                              + Heading
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); addBlock(sIdx, 'BODY'); }}
                              className="text-[9px] text-brand hover:underline font-semibold"
                            >
                              + Body
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); addBlock(sIdx, 'BUTTON'); }}
                              className="text-[9px] text-brand hover:underline font-semibold"
                            >
                              + Button
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add New Section buttons */}
              <div className="mt-4 border-t border-white/[0.08] pt-4">
                <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest block mb-2">Add Section Grid</span>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => addSection('HERO')}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-white/10 hover:border-brand/40 px-3 py-2 text-xs font-semibold hover:bg-brand/5 text-zinc-400 hover:text-brand transition-all"
                  >
                    <Plus className="h-3 w-3" />
                    Hero
                  </button>
                  <button
                    onClick={() => addSection('FEATURED_PRODUCTS')}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-white/10 hover:border-brand/40 px-3 py-2 text-xs font-semibold hover:bg-brand/5 text-zinc-400 hover:text-brand transition-all"
                  >
                    <Plus className="h-3 w-3" />
                    Products Grid
                  </button>
                  <button
                    onClick={() => addSection('RICHTEXT')}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-white/10 hover:border-brand/40 px-3 py-2 text-xs font-semibold hover:bg-brand/5 text-zinc-400 hover:text-brand transition-all"
                  >
                    <Plus className="h-3 w-3" />
                    Rich Text
                  </button>
                  <button
                    onClick={() => addSection('BANNER')}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-white/10 hover:border-brand/40 px-3 py-2 text-xs font-semibold hover:bg-brand/5 text-zinc-400 hover:text-brand transition-all"
                  >
                    <Plus className="h-3 w-3" />
                    Banner
                  </button>
                </div>
              </div>

            </div>

          </div>

          {/* Center/Right canvas: Simulator preview + properties editor */}
          <div className="lg:col-span-8 flex flex-col gap-6">
            
            {/* Editor properties inspector (only shown if a section or block is selected) */}
            {(selectedSectionIdx !== null || selectedBlockIdx !== null) && (
              <div className="rounded-2xl border border-brand/20 bg-brand/5 p-5">
                <div className="flex items-center justify-between border-b border-white/[0.08] pb-3 mb-4">
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-brand" />
                    <span className="text-xs font-bold uppercase tracking-wider text-brand">
                      {selectedBlockIdx !== null 
                        ? `Block Settings: ${selectedPage.sections?.[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].type}`
                        : `Section Settings: ${selectedPage.sections?.[selectedSectionIdx!].type}`
                      }
                    </span>
                  </div>
                  <button
                    onClick={() => { setSelectedSectionIdx(null); setSelectedBlockIdx(null); }}
                    className="text-[10px] text-zinc-400 hover:text-zinc-200 uppercase font-semibold"
                  >
                    Close Panel
                  </button>
                </div>

                {/* Section properties inputs */}
                {selectedSectionIdx !== null && selectedBlockIdx === null && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {selectedPage.sections?.[selectedSectionIdx].type === 'HERO' && (
                      <>
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Alignment</label>
                          <select
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedSectionIdx].settings.alignment || 'center'}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedSectionIdx].settings.alignment = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          >
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Overlay Opacity (0 to 1)</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="1"
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedSectionIdx].settings.overlayOpacity ?? 0.6}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedSectionIdx].settings.overlayOpacity = parseFloat(e.target.value);
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Background Image URL</label>
                          <input
                            type="text"
                            placeholder="https://example.com/image.jpg"
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200 font-mono"
                            value={selectedPage.sections[selectedSectionIdx].settings.backgroundImage || ''}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedSectionIdx].settings.backgroundImage = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                      </>
                    )}

                    {selectedPage.sections?.[selectedSectionIdx].type === 'FEATURED_PRODUCTS' && (
                      <>
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Section Title</label>
                          <input
                            type="text"
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedSectionIdx].settings.title || ''}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedSectionIdx].settings.title = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Section Subtitle</label>
                          <input
                            type="text"
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedSectionIdx].settings.subtitle || ''}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedSectionIdx].settings.subtitle = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                      </>
                    )}

                    {selectedPage.sections?.[selectedSectionIdx].type === 'RICHTEXT' && (
                      <>
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Padding Vertical</label>
                          <select
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedSectionIdx].settings.paddingY || 'py-16'}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedSectionIdx].settings.paddingY = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          >
                            <option value="py-8">Small (py-8)</option>
                            <option value="py-16">Standard (py-16)</option>
                            <option value="py-24">Large (py-24)</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Background theme</label>
                          <select
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedSectionIdx].settings.background || 'bg-white'}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedSectionIdx].settings.background = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          >
                            <option value="bg-white">Pure White (bg-white)</option>
                            <option value="bg-slate-50">Soft Grey (bg-slate-50)</option>
                            <option value="bg-slate-900">Deep Slate (bg-slate-900)</option>
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Block properties inputs */}
                {selectedBlockIdx !== null && (
                  <div className="grid grid-cols-1 gap-4">
                    {selectedPage.sections?.[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].type === 'HEADING' && (
                      <div>
                        <label className="text-[10px] font-bold text-zinc-400 block mb-1">Heading Text</label>
                        <input
                          type="text"
                          className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                          value={selectedPage.sections[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.text || ''}
                          onChange={(e) => {
                            const updated = [...(selectedPage.sections || [])];
                            updated[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.text = e.target.value;
                            setSelectedPage({ ...selectedPage, sections: updated });
                          }}
                        />
                      </div>
                    )}

                    {selectedPage.sections?.[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].type === 'BODY' && (
                      <div>
                        <label className="text-[10px] font-bold text-zinc-400 block mb-1">Paragraph Text</label>
                        <textarea
                          rows={3}
                          className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                          value={selectedPage.sections[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.text || ''}
                          onChange={(e) => {
                            const updated = [...(selectedPage.sections || [])];
                            updated[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.text = e.target.value;
                            setSelectedPage({ ...selectedPage, sections: updated });
                          }}
                        />
                      </div>
                    )}

                    {selectedPage.sections?.[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].type === 'BUTTON' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Button Label</label>
                          <input
                            type="text"
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.text || ''}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.text = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Redirect Path / URL</label>
                          <input
                            type="text"
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.url || ''}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.url = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {selectedPage.sections?.[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].type === 'HERO_SLIDE' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Headline Text</label>
                          <input
                            type="text"
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.headline || ''}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.headline = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Tagline Badge Text</label>
                          <input
                            type="text"
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.tagline || ''}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.tagline = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Slide Description</label>
                          <textarea
                            rows={2}
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.description || ''}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.description = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Button text</label>
                          <input
                            type="text"
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.ctaText || ''}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.ctaText = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Button link path</label>
                          <input
                            type="text"
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.ctaUrl || ''}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.ctaUrl = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {selectedPage.sections?.[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].type === 'PRODUCT_CARD' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Map to Catalog Product</label>
                          <select
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.title || ''}
                            onChange={(e) => {
                              const p = products.find((prod) => prod.title === e.target.value);
                              if (p) {
                                const updated = [...(selectedPage.sections || [])];
                                updated[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content = {
                                  title: p.title,
                                  price: p.price,
                                  category: 'Catalog',
                                  imageUrl: p.images[0] || '',
                                  badge: p.stock > 0 ? 'NEW' : 'OUT OF STOCK',
                                };
                                setSelectedPage({ ...selectedPage, sections: updated });
                              }
                            }}
                          >
                            <option value="">-- Choose dynamic product --</option>
                            {products.map((p) => (
                              <option key={p.id} value={p.title}>
                                {p.title} (${p.price})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Override Card Title</label>
                          <input
                            type="text"
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.title || ''}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.title = e.target.value;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-zinc-400 block mb-1">Override Card Price</label>
                          <input
                            type="number"
                            className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                            value={selectedPage.sections[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.price || 0}
                            onChange={(e) => {
                              const updated = [...(selectedPage.sections || [])];
                              updated[selectedBlockIdx.secIdx].blocks[selectedBlockIdx.blkIdx].content.price = parseFloat(e.target.value) || 0;
                              setSelectedPage({ ...selectedPage, sections: updated });
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Save sub-element state block */}
                <div className="mt-4 border-t border-white/[0.08] pt-3 flex justify-between">
                  <button
                    onClick={() => { setSelectedSectionIdx(null); setSelectedBlockIdx(null); }}
                    className="px-3 py-1.5 rounded bg-zinc-800 text-[10px] font-bold text-zinc-400 hover:text-zinc-200"
                  >
                    Done Editing
                  </button>
                  {selectedBlockIdx !== null && (
                    <button
                      onClick={() => removeBlock(selectedBlockIdx.secIdx, selectedBlockIdx.blkIdx)}
                      className="px-3 py-1.5 rounded bg-red-500/10 hover:bg-red-500/20 text-[10px] font-bold text-red-400"
                    >
                      Delete Block
                    </button>
                  )}
                </div>

              </div>
            )}

            {/* Central Simulator Canvas Frame */}
            <div className="rounded-2xl border border-white/[0.08] bg-dark-panel overflow-hidden shadow-2xl">
              
              {/* Simulator Browser Bar */}
              <div className="bg-zinc-900 border-b border-white/[0.08] px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500/40 inline-block"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/40 inline-block"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/40 inline-block"></span>
                </div>
                <div className="bg-white/5 border border-white/[0.05] rounded-lg text-[10px] font-mono text-zinc-500 px-6 py-1 w-2/3 text-center truncate">
                  https://{org?.custom_domain || `${org?.slug}.nexuscommerce.app`}/{selectedPage.slug === 'home' ? '' : selectedPage.slug}
                </div>
                <span className="text-[10px] text-brand bg-brand/10 px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                  Live Simulator
                </span>
              </div>

              {/* simulated storefront page layout */}
              <div className="bg-white min-h-[45vh] max-h-[60vh] overflow-y-auto text-slate-800 flex flex-col font-sans select-none">
                
                {/* Simulated Header */}
                <header className="border-b border-slate-100 py-3 px-6 bg-white flex items-center justify-between sticky top-0 z-30">
                  <span className="text-sm font-black tracking-tight" style={{ color: org?.brand_color || '#3b82f6' }}>
                    {org?.name || 'My Storefront'}
                  </span>
                  <nav className="flex items-center gap-4 text-[10px] font-semibold text-slate-500">
                    <span className="text-slate-800 border-b-2 border-slate-800 pb-0.5">Home</span>
                    <span>Catalog</span>
                    <span>Our Story</span>
                  </nav>
                  {org?.whatsapp_number && (
                    <span className="px-2.5 py-1 text-[9px] font-bold text-white rounded-full bg-emerald-500">
                      💬 WhatsApp
                    </span>
                  )}
                </header>

                {/* Dynamic sections mapping in simulator */}
                <main className="flex-grow">
                  {selectedPage.sections && selectedPage.sections.length > 0 ? (
                    selectedPage.sections.map((sec, sIdx) => {
                      const isSecSelected = selectedSectionIdx === sIdx;
                      
                      return (
                        <div
                          key={sIdx}
                          onClick={() => { setSelectedSectionIdx(sIdx); setSelectedBlockIdx(null); }}
                          className={`relative border-2 border-transparent transition-all ${
                            isSecSelected ? 'border-brand bg-brand/5' : 'hover:border-slate-200'
                          }`}
                        >
                          {/* Section Label */}
                          <div className="absolute top-2 left-2 z-10 bg-slate-900/80 backdrop-blur text-[8px] font-mono text-white px-1.5 py-0.5 rounded opacity-0 hover:opacity-100 transition-opacity">
                            Section: {sec.type}
                          </div>

                          {/* HERO RESOLUTION */}
                          {sec.type === 'HERO' && (
                            <div
                              className="py-12 px-6 text-white text-center flex flex-col items-center justify-center min-h-[220px]"
                              style={{
                                backgroundImage: `linear-gradient(135deg, ${org?.brand_color || '#3b82f6'}dd, #1e293b)`,
                              }}
                            >
                              {sec.blocks.map((blk, bIdx) => {
                                if (blk.type === 'HERO_SLIDE') {
                                  return (
                                    <div key={bIdx} className="max-w-md mx-auto">
                                      <span className="text-[9px] uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">
                                        {blk.content.tagline || 'Summer collection'}
                                      </span>
                                      <h2 className="text-xl md:text-2xl font-black mt-2 leading-tight">
                                        {blk.content.headline || 'Welcome to Storefront'}
                                      </h2>
                                      <p className="text-[10px] text-slate-300 mt-2">
                                        {blk.content.description || 'Custom crafted items details.'}
                                      </p>
                                      {blk.content.ctaText && (
                                        <span
                                          className="inline-block mt-4 px-4 py-2 text-[9px] font-bold text-white rounded-lg"
                                          style={{ backgroundColor: org?.brand_color || '#3b82f6' }}
                                        >
                                          {blk.content.ctaText}
                                        </span>
                                      )}
                                    </div>
                                  );
                                }
                                return null;
                              })}
                              {sec.blocks.length === 0 && (
                                <span className="text-slate-400 text-xs italic">Empty Hero. Click to select and add slides.</span>
                              )}
                            </div>
                          )}

                          {/* FEATURED PRODUCTS RESOLUTION */}
                          {sec.type === 'FEATURED_PRODUCTS' && (
                            <div className="py-10 px-6 bg-slate-50">
                              <div className="text-center mb-6">
                                <h3 className="text-sm font-bold text-slate-800">{sec.settings.title || 'Our Products'}</h3>
                                <p className="text-[9px] text-slate-400">{sec.settings.subtitle || 'Best offers'}</p>
                              </div>
                              <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
                                {sec.blocks.filter(b => b.type === 'PRODUCT_CARD').map((blk, bIdx) => (
                                  <div key={bIdx} className="bg-white border border-slate-100 rounded-xl overflow-hidden p-3 flex flex-col justify-between">
                                    <div className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 mb-2 relative">
                                      {blk.content.imageUrl ? (
                                        <img src={blk.content.imageUrl} alt={blk.content.title} className="w-full h-full object-cover rounded-lg" />
                                      ) : (
                                        <span className="text-[8px]">Image Placeholder</span>
                                      )}
                                      {blk.content.badge && (
                                        <span className="absolute top-1 left-1 bg-red-500 text-white text-[7px] font-bold px-1 rounded">
                                          {blk.content.badge}
                                        </span>
                                      )}
                                    </div>
                                    <div>
                                      <h4 className="text-[10px] font-bold text-slate-700 truncate">{blk.content.title}</h4>
                                      <div className="flex items-center justify-between mt-1">
                                        <span className="text-[10px] font-extrabold text-slate-900">${blk.content.price}</span>
                                        <span
                                          className="text-[8px] font-bold text-white px-2 py-0.5 rounded"
                                          style={{ backgroundColor: org?.brand_color || '#3b82f6' }}
                                        >
                                          Add
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {sec.blocks.filter(b => b.type === 'PRODUCT_CARD').length === 0 && (
                                  <div className="col-span-full text-center text-slate-400 text-[10px] italic bg-white border border-dashed rounded p-6">
                                    No product cards matched. Select block to choose products.
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* RICH TEXT RESOLUTION */}
                          {sec.type === 'RICHTEXT' && (
                            <div className={`py-12 px-6 text-center ${sec.settings.background || 'bg-white'}`}>
                              <div className="max-w-sm mx-auto flex flex-col items-center">
                                {sec.blocks.map((blk, bIdx) => {
                                  if (blk.type === 'HEADING') {
                                    return <h3 key={bIdx} className="text-sm font-extrabold text-slate-800 mb-2">{blk.content.text}</h3>;
                                  }
                                  if (blk.type === 'BODY') {
                                    return <p key={bIdx} className="text-[10px] text-slate-500 mb-3 leading-relaxed">{blk.content.text}</p>;
                                  }
                                  if (blk.type === 'BUTTON') {
                                    return (
                                      <span
                                        key={bIdx}
                                        className="inline-block px-4 py-1.5 text-[9px] font-bold text-white rounded"
                                        style={{ backgroundColor: org?.brand_color || '#3b82f6' }}
                                      >
                                        {blk.content.text}
                                      </span>
                                    );
                                  }
                                  return null;
                                })}
                                {sec.blocks.length === 0 && (
                                  <span className="text-slate-400 text-xs italic">Empty Rich Text grid. Click to edit content.</span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* BANNER RESOLUTION */}
                          {sec.type === 'BANNER' && (
                            <div className="bg-slate-900 text-white py-6 px-6 border-y border-slate-800">
                              <div className="flex items-center justify-between gap-4 max-w-md mx-auto">
                                <div>
                                  <h4 className="text-[10px] font-bold text-slate-100">
                                    {sec.blocks.find(b => b.type === 'BANNER_CONTENT')?.content.title || 'Free shipping on orders over $150'}
                                  </h4>
                                  <p className="text-[8px] text-slate-400">
                                    {sec.blocks.find(b => b.type === 'BANNER_CONTENT')?.content.subtitle || 'Promo applied at checkout.'}
                                  </p>
                                </div>
                                <span className="px-3 py-1 bg-white text-slate-900 font-bold rounded text-[8px]">
                                  Claim Offer
                                </span>
                              </div>
                            </div>
                          )}

                        </div>
                      );
                    })
                  ) : (
                    <div className="py-16 text-center text-slate-400 text-xs italic">
                      Empty template. Click "Add Section" to design the storefront layout!
                    </div>
                  )}
                </main>

                {/* Simulated Footer */}
                <footer className="bg-slate-900 text-white py-8 px-6 border-t border-slate-800 text-[10px]">
                  <div className="max-w-md mx-auto flex justify-between items-center gap-4">
                    <div>
                      <span className="font-extrabold" style={{ color: org?.brand_color || '#3b82f6' }}>
                        {org?.name}
                      </span>
                      <p className="text-[8px] text-slate-400 mt-1">&copy; {new Date().getFullYear()} All rights reserved.</p>
                    </div>
                    <span className="text-[8px] text-slate-500 font-mono">Powered by Nexus Commerce</span>
                  </div>
                </footer>

              </div>

            </div>

          </div>

        </div>
      )}

      {/* ==============================================================================
          TAB 2: PRODUCTS CATALOG MANAGER
          ============================================================================== */}
      {activeTab === 'products' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Left Panel: Catalog list */}
          <div className="lg:col-span-8 rounded-2xl border border-white/[0.08] bg-dark-panel p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400">Products Catalog ({products.length})</h3>
              <button
                onClick={() => openEditProduct(null)}
                className="flex items-center gap-1 text-xs text-brand font-bold hover:underline"
              >
                <PlusCircle className="h-4 w-4" />
                Add Catalog Product
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-zinc-400">
                <thead className="bg-white/5 uppercase text-zinc-500 font-bold">
                  <tr>
                    <th className="p-3">Product Name</th>
                    <th className="p-3">Slug</th>
                    <th className="p-3">Price</th>
                    <th className="p-3">Inventory</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.05]">
                  {products.map((p) => (
                    <tr key={p.id} className="hover:bg-white/[0.02]">
                      <td className="p-3 font-semibold text-zinc-200">{p.title}</td>
                      <td className="p-3 font-mono text-[10px] text-zinc-500">/{p.slug}</td>
                      <td className="p-3 font-bold text-zinc-300">${p.price}</td>
                      <td className="p-3 font-mono">{p.stock} units</td>
                      <td className="p-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                          p.isActive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                        }`}>
                          {p.isActive ? 'Active' : 'Draft'}
                        </span>
                      </td>
                      <td className="p-3 text-right flex justify-end gap-2">
                        <button
                          onClick={() => openEditProduct(p)}
                          className="px-2.5 py-1 rounded bg-white/5 hover:bg-white/10 text-[10px] font-bold text-zinc-300"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => p.id && deleteProduct(p.id)}
                          className="p-1 hover:bg-red-500/10 rounded text-zinc-500 hover:text-red-400"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {products.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center italic text-zinc-500">
                        No product catalog items registered yet. Click "Add Catalog Product" to seed one!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right Panel: CRUD Form */}
          <div className="lg:col-span-4 rounded-2xl border border-white/[0.08] bg-dark-panel p-5 flex flex-col gap-4">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400">
              {editingProduct ? 'Edit Catalog Product' : 'Create Catalog Product'}
            </h3>

            <div className="flex flex-col gap-3">
              <div>
                <label className="text-[10px] font-bold text-zinc-400 block mb-1">Product Title</label>
                <input
                  type="text"
                  placeholder="e.g. Air Max Runner"
                  className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                  value={prodTitle}
                  onChange={(e) => handleTitleChange(e.target.value, 'prod')}
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-zinc-400 block mb-1">Product URL Slug</label>
                <input
                  type="text"
                  placeholder="air-max-runner"
                  className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200 font-mono"
                  value={prodSlug}
                  onChange={(e) => setProdSlug(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-zinc-400 block mb-1">Price ($ USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200 font-mono"
                    value={prodPrice}
                    onChange={(e) => setProdPrice(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-zinc-400 block mb-1">Stock Level</label>
                  <input
                    type="number"
                    className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200 font-mono"
                    value={prodStock}
                    onChange={(e) => setProdStock(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold text-zinc-400 block mb-1">Image URL</label>
                <input
                  type="text"
                  placeholder="https://images.unsplash.com/photo-..."
                  className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200 font-mono"
                  value={prodImage}
                  onChange={(e) => setProdImage(e.target.value)}
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-zinc-400 block mb-1">Product Description</label>
                <textarea
                  rows={3}
                  placeholder="Details of the product design, size, and collections..."
                  className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                  value={prodDesc}
                  onChange={(e) => setProdDesc(e.target.value)}
                />
              </div>

              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="prodActive"
                  checked={prodActive}
                  onChange={(e) => setProdActive(e.target.checked)}
                  className="rounded border-white/10 bg-zinc-900 text-brand focus:ring-brand"
                />
                <label htmlFor="prodActive" className="text-xs text-zinc-300 font-semibold cursor-pointer">
                  Publish to Catalog Storefront
                </label>
              </div>

              <div className="mt-4 flex gap-2 pt-3 border-t border-white/[0.05]">
                <button
                  onClick={saveProduct}
                  disabled={saving}
                  className="flex-grow flex items-center justify-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-xs font-bold text-white hover:bg-brand-hover disabled:opacity-50"
                >
                  {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                  {editingProduct ? 'Update Product' : 'Add to Catalog'}
                </button>
                {editingProduct && (
                  <button
                    onClick={() => openEditProduct(null)}
                    className="px-3 py-2 rounded-lg bg-zinc-800 text-xs font-semibold text-zinc-400 hover:text-zinc-200"
                  >
                    Cancel
                  </button>
                )}
              </div>

            </div>
          </div>

        </div>
      )}

      {/* ==============================================================================
          TAB 3: THEME & BRANDING SETTINGS
          ============================================================================== */}
      {activeTab === 'branding' && org && (
        <div className="rounded-2xl border border-white/[0.08] bg-dark-panel p-5 max-w-2xl">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Theme & Branding customization</h3>

          <div className="flex flex-col gap-4">
            <div>
              <label className="text-[10px] font-bold text-zinc-400 block mb-1">Store Display Name</label>
              <input
                type="text"
                className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                value={org.name || ''}
                onChange={(e) => setOrg({ ...org, name: e.target.value })}
              />
            </div>

            <div>
              <label className="text-[10px] font-bold text-zinc-400 block mb-1">Brand Accent Primary Color</label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  className="h-8 w-12 rounded border-0 bg-transparent cursor-pointer"
                  value={org.brand_color || '#3b82f6'}
                  onChange={(e) => setOrg({ ...org, brand_color: e.target.value })}
                />
                <input
                  type="text"
                  className="rounded-lg bg-zinc-900 border border-white/10 px-3 py-1.5 text-xs text-zinc-200 font-mono w-28"
                  value={org.brand_color || '#3b82f6'}
                  onChange={(e) => setOrg({ ...org, brand_color: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-zinc-400 block mb-1">WhatsApp Customer Support Number</label>
              <input
                type="text"
                placeholder="e.g. 20123456789"
                className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200 font-mono"
                value={org.whatsapp_number || ''}
                onChange={(e) => setOrg({ ...org, whatsapp_number: e.target.value })}
              />
              <p className="mt-1 text-[10px] text-zinc-500">
                Adds a quick WhatsApp chat bubble to the header so shoppers can contact you instantly in one click.
              </p>
            </div>

            <div className="mt-4 border-t border-white/[0.05] pt-4">
              <button
                onClick={saveStorefront}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-xs font-bold text-white hover:bg-brand-hover"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save Accent Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==============================================================================
          TAB 4: DOMAIN & GENERAL SETTINGS
          ============================================================================== */}
      {activeTab === 'settings' && org && (
        <div className="rounded-2xl border border-white/[0.08] bg-dark-panel p-5 max-w-2xl">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Domain Settings</h3>

          <div className="flex flex-col gap-4">
            <div>
              <label className="text-[10px] font-bold text-zinc-400 block mb-1">SaaS Subdomain</label>
              <div className="flex items-center">
                <input
                  type="text"
                  disabled
                  className="rounded-l-lg bg-zinc-900/50 border border-white/10 px-3 py-2 text-xs text-zinc-500 font-mono w-1/2"
                  value={org.slug || ''}
                />
                <span className="rounded-r-lg bg-zinc-800 border-y border-r border-white/10 px-3 py-2 text-xs text-zinc-400 font-mono">
                  .nexuscommerce.app
                </span>
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-zinc-400 block mb-1">Mapped Custom Domain</label>
              <input
                type="text"
                placeholder="e.g. mystore.com"
                className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200 font-mono"
                value={org.custom_domain || ''}
                onChange={(e) => setOrg({ ...org, custom_domain: e.target.value })}
              />
              <p className="mt-1 text-[10px] text-zinc-500">
                Point your domain's <strong>A Record</strong> to the server IP: <strong className="text-zinc-300">178.16.129.216</strong>. Traffic is resolved dynamically with SSL termination automatically.
              </p>
            </div>

            <div className="mt-4 border-t border-white/[0.05] pt-4">
              <button
                onClick={saveStorefront}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-xs font-bold text-white hover:bg-brand-hover"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Map Custom Domain
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==============================================================================
          MODAL: NEW STORE PAGE
          ============================================================================== */}
      {showNewPageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-dark-panel p-6 shadow-2xl">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-300 mb-4 flex items-center gap-2">
              <FileText className="h-4 w-4 text-brand" />
              Register Storefront Page
            </h3>
            
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-[10px] font-bold text-zinc-400 block mb-1">Page Title</label>
                <input
                  type="text"
                  placeholder="e.g. Summer collection"
                  className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200"
                  value={newPageTitle}
                  onChange={(e) => handleTitleChange(e.target.value, 'page')}
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-zinc-400 block mb-1">Page path Slug</label>
                <input
                  type="text"
                  placeholder="summer-collection"
                  className="w-full rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-200 font-mono"
                  value={newPageSlug}
                  onChange={(e) => setNewPageSlug(e.target.value)}
                />
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={createPage}
                  disabled={saving || !newPageTitle}
                  className="flex-grow flex items-center justify-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-xs font-bold text-white hover:bg-brand-hover"
                >
                  {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                  Create Page
                </button>
                <button
                  onClick={() => setShowNewPageModal(false)}
                  className="px-4 py-2 rounded-lg bg-zinc-800 text-xs font-semibold text-zinc-400 hover:text-zinc-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
